import { useCallback } from 'react';
import { generatePath } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { useCommonTranslation } from 'c-translation';
import { PostAuthRoutes } from 'c-routes';
import type { UsageOrganisationType, UsagePeriod } from 'c-sdk';

export const organisationLink = (type: UsageOrganisationType, id: number) =>
    generatePath(PostAuthRoutes.Admin.UsageOrganisation, { type, id });

export const userLink = (id: number) => generatePath(PostAuthRoutes.Admin.UsageUser, { id });

/** The periods the usage pages offer, in days. The API caps a period at 366. */
export const USAGE_PERIODS = [7, 30, 90] as const;

export const DEFAULT_PERIOD_DAYS = 30;

/** `from`/`to` as the API takes them: whole days, the last `days` up to and including today. */
export const periodFor = (days: number, now: Date = new Date()): UsagePeriod => ({
    from: format(subDays(now, days - 1), 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd'),
});

/** Percentage change, rounded; null when there is nothing to compare against. */
export const deltaPercent = (current: number, previous: number): number | null => {
    if (previous === 0) return null;
    return Math.round(((current - previous) / previous) * 100);
};

/** `part` of `whole` as a 0–100 percentage for a bar's width. */
export const percentOf = (part: number, whole: number): number =>
    whole > 0 ? Math.min(100, Math.round((part / whole) * 100)) : 0;

/** Formats milliseconds as `45s`, `11m 40s` or `14h 10m`, through the translations. */
export const useFormatDuration = () => {
    const t = useCommonTranslation();

    return useCallback(
        (ms: number | null | undefined): string => {
            if (ms == null || ms <= 0) return t('Modules.Admin.Usage.duration.none');
            const totalSeconds = Math.round(ms / 1000);
            const h = Math.floor(totalSeconds / 3600);
            const m = Math.floor((totalSeconds % 3600) / 60);
            const s = totalSeconds % 60;
            if (h > 0) return t('Modules.Admin.Usage.duration.hours', { h, m });
            if (m > 0) return t('Modules.Admin.Usage.duration.minutes', { m, s });
            return t('Modules.Admin.Usage.duration.seconds', { s });
        },
        [t],
    );
};
